'use client';

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import {
  createContext,
  useEffect,
  useState,
  useReducer,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import { useLocalStorage } from 'react-use';
import { useTimer } from 'use-timer';

import {
  convertLineUpForMutation,
  convertLogsForMutation,
} from '@/utils/data/matchMode';
import { usePathname } from '@/i18n/navigation';
import filterByReference from '@/utils/data/filterByReference';
import type { Match, MatchLog, MatchScore, Player, Team } from '@/types/domain';

export interface MatchState {
  id: string | null;
  uid: string | null;
  now: number;
  visible: boolean;
  start: boolean;
  startTime: string | null;
  endTime: string | null;
  paused: boolean;
  halfTime: boolean;
  secondHalf: boolean;
  pauseOverlay: boolean;
  warning: boolean;
  score: MatchScore;
  initialLineUp: Player[];
  currentLineUp: Player[];
  substitutes: Player[];
  absents: Player[];
  logs: MatchLog[];
  time?: number;
  [key: string]: unknown;
}

type MatchAction =
  | { type: 'MATCH_LOG_OPEN' }
  | { type: 'MATCH_LOG_CLOSE' }
  | { type: 'MATCH_START'; start: boolean; startTime: string | null }
  | { type: 'MATCH_END' }
  | { type: 'MATCH_ENDTIME'; endTime: string | null }
  | { type: 'MATCH_PAUSED'; paused: boolean }
  | { type: 'MATCH_PAUSE_OVERLAY'; pauseOverlay: boolean }
  | { type: 'MATCH_HALFTIME'; halfTime: boolean }
  | { type: 'MATCH_WARNING'; warning: boolean }
  | { type: 'MATCH_SECONDHALF'; secondHalf: boolean }
  | { type: 'MATCH_UPDATE_SCORE'; score: MatchScore }
  | { type: 'MATCH_UPDATE_ABSENTS'; absents: Player[] }
  | { type: 'MATCH_UPDATE_SUBSTITUTES'; substitutes: Player[] }
  | { type: 'MATCH_UPDATE_INITIAL_LINEUP'; initialLineUp: Player[] }
  | { type: 'MATCH_UPDATE_LINEUP'; currentLineUp: Player[] }
  | { type: 'MATCH_UPDATE_LOGS'; logs: MatchLog[] }
  | { type: 'MATCH_UPDATE_TIME'; time: number }
  | { type: 'MATCH_INITIAL_LOAD'; data: Partial<MatchState> }
  | { type: 'MATCH_UPDATE_MATCH'; data: Partial<MatchState> }
  | { type: 'MATCH_SAVE_ENTRY'; data?: unknown }
  | { type: 'MATCH_RESET' };

interface MatchTimer {
  time: number;
  start: () => void;
  pause: () => void;
  reset: () => void;
  status: string;
  advanceTime: (delta: number) => void;
}

/** The context value passed to saveMatch/refreshMatch by consumers (state + match). */
interface SaveMatchArg {
  match: Match;
  logs: MatchLog[];
  score?: MatchScore;
  startTime?: string | null;
  endTime?: string | null;
  initialLineUp?: Player[];
  visible?: boolean;
}

export interface MatchContextValue extends MatchState {
  saveMatch: (state: SaveMatchArg) => Promise<void>;
  saveLocalMatchLogState: () => void;
  setMatchTime: (seconds: number) => void;
  refreshMatch: (state: { match: Match }) => Promise<void>;
  dispatch: Dispatch<MatchAction>;
  enabled: boolean;
  team: Team;
  match: Match;
  setMatchData: Dispatch<SetStateAction<Match>>;
  timer: MatchTimer;
  tabNav: string;
  setTabNav: Dispatch<SetStateAction<string>>;
  showSidebar: boolean;
  setShowSidebar: Dispatch<SetStateAction<boolean>>;
  sidebarTabNav: string;
  setSidebarTabNav: Dispatch<SetStateAction<string>>;
  showEvaluation: boolean;
  setShowEvaluation: Dispatch<SetStateAction<boolean>>;
  selectedPlayer: Player | null;
  setSelectedPlayer: Dispatch<SetStateAction<Player | null>>;
  playerFormationRef: unknown;
  setPlayerFormationRef: Dispatch<SetStateAction<unknown>>;
}

const initialMatchState: MatchState = {
  id: null,
  uid: null,
  now: Date.now(),
  visible: false,
  start: false,
  startTime: null,
  endTime: null,
  paused: false,
  halfTime: false,
  secondHalf: false,
  pauseOverlay: false,
  warning: false,
  score: {
    team: 0,
    opponent: 0,
  },
  initialLineUp: [],
  currentLineUp: [],
  substitutes: [], // players that are not in the current line up but are included initial line up
  absents: [], // players from the team that are not in initial line up
  logs: [],
};

// See PrintContext for the cast-default rationale.
export const MatchContext = createContext<MatchContextValue>(
  undefined as unknown as MatchContextValue,
);

const matchReducer = (state: MatchState, action: MatchAction): MatchState => {
  switch (action.type) {
    case 'MATCH_LOG_OPEN': {
      return { ...state, visible: true };
    }
    case 'MATCH_LOG_CLOSE': {
      return { ...state, visible: false };
    }
    case 'MATCH_START': {
      return { ...state, start: action.start, startTime: action.startTime };
    }
    case 'MATCH_END': {
      return { ...state };
    }
    case 'MATCH_ENDTIME': {
      return { ...state, endTime: action.endTime };
    }
    case 'MATCH_PAUSED': {
      return { ...state, paused: action.paused };
    }
    case 'MATCH_PAUSE_OVERLAY': {
      return { ...state, pauseOverlay: action.pauseOverlay };
    }
    case 'MATCH_HALFTIME': {
      return { ...state, halfTime: action.halfTime };
    }
    case 'MATCH_WARNING': {
      return { ...state, warning: action.warning };
    }
    case 'MATCH_SECONDHALF': {
      return { ...state, secondHalf: action.secondHalf };
    }
    case 'MATCH_UPDATE_SCORE': {
      return { ...state, score: action.score };
    }
    case 'MATCH_UPDATE_ABSENTS': {
      return { ...state, absents: action.absents };
    }
    case 'MATCH_UPDATE_SUBSTITUTES': {
      return { ...state, substitutes: action.substitutes };
    }
    case 'MATCH_UPDATE_INITIAL_LINEUP': {
      return { ...state, initialLineUp: action.initialLineUp };
    }
    case 'MATCH_UPDATE_LINEUP': {
      return { ...state, currentLineUp: action.currentLineUp };
    }
    case 'MATCH_UPDATE_LOGS': {
      return { ...state, logs: action.logs };
    }
    case 'MATCH_UPDATE_TIME': {
      return { ...state, time: action.time };
    }
    case 'MATCH_INITIAL_LOAD': {
      return { ...state, ...action.data };
    }
    case 'MATCH_UPDATE_MATCH': {
      return { ...state, ...action.data };
    }
    case 'MATCH_SAVE_ENTRY': {
      return { ...state };
    }
    case 'MATCH_RESET': {
      return {
        ...state,
        ...initialMatchState,
      };
    }
  }
};

interface MatchProviderProps {
  children: ReactNode;
  match?: Match;
  team?: Team;
  enabled?: boolean;
}

const MatchProvider = (props: MatchProviderProps) => {
  const { children, match = {}, team = {}, enabled = false } = props;

  const [state, dispatch] = useReducer(matchReducer, initialMatchState);
  const [matchData, setMatchData] = useState<Match>(match);

  // See https://github.com/thibaultboursier/use-timer
  const { time, start, pause, reset, status, advanceTime } = useTimer({
    autostart: false,
    initialTime: 0,
  });

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [playerFormationRef, setPlayerFormationRef] = useState<unknown>(null);

  const [tabNav, setTabNav] = useState('formation');
  const [sidebarTabNav, setSidebarTabNav] = useState('substitute');
  const [showSidebar, setShowSidebar] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);

  const [matchLogState, setMatchLogState] = useLocalStorage<MatchState>(
    match.uid || 'matchLog',
  );
  const pathname = usePathname();

  useEffect(() => {
    if (localStorage.getItem('matchLog')) {
      localStorage.removeItem('matchLog');
    }
    if (matchData.matchStatus === 'ended') {
      dispatch({ type: 'MATCH_RESET' });
    }
    if (matchLogState) {
      const timer = matchLogState.timer as
        { status?: string; time?: number } | undefined;
      if (status !== 'RUNNING' && timer?.status === 'RUNNING') {
        start();
      }
      if (status === 'STOPPED' && timer?.status === 'PAUSED') {
        start();
        pause();
      }

      if (time === 0 && timer?.time) {
        advanceTime(timer.time);
      }
    }

    return () => {
      /* no-op */
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger; adding the flagged deps would shift this shared provider's effect/identity timing across every match consumer
  }, [matchData.uid, matchLogState]);

  useEffect(() => {
    document.body.style.overflow = state.visible ? 'hidden' : '';
  }, [state.visible]);

  const saveLocalMatchLogState = () => {
    if (!state.start) return;

    const localStorageState = {
      ...state,
      timer: {
        status,
        time,
      },
    };
    setMatchLogState(localStorageState);
  };

  useEffect(() => {
    if (match.matchStatus === 'ended') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      localStorage.removeItem(match.uid!);
    } else {
      saveLocalMatchLogState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger; adding the flagged deps would shift this shared provider's effect/identity timing across every match consumer
  }, [time, status]);

  useEffect(() => {
    /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    if (
      pathname?.startsWith('/match/id/') &&
      matchData.matchStatus === 'ongoing' &&
      JSON.stringify(matchData.matchScore![0]) !== JSON.stringify(state.score)
    ) {
      saveLocalMatchLogState();
    }
    /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger; adding the flagged deps would shift this shared provider's effect/identity timing across every match consumer
  }, [state.score, matchData]);

  const setMatchTime = (seconds: number) => {
    if (!seconds) return;
    advanceTime(seconds);
  };

  const saveMatch = useCallback(
    async (state: SaveMatchArg) => {
      if (!state.match.id) {
        console.error('Match without id');
        return;
      }
      let fieldsToUpdate: Record<string, unknown> = {};

      // If the match has ended, only allow matchlogs to be updated
      fieldsToUpdate =
        state.match.matchStatus === 'ended'
          ? {
              matchLogs: convertLogsForMutation(state.logs),
            }
          : {
              matchDay: state.match.matchDay,
              score: [{ col1: state.score?.team, col2: state.score?.opponent }],
              matchStatus: state.endTime ? 'ended' : 'ongoing',
              matchStartedTime: state.startTime,
              matchEndedTime: state.endTime,
              initialLineUp: convertLineUpForMutation(
                state.initialLineUp,
                state.visible,
              ),
              matchLogs: convertLogsForMutation(state.logs),
            };
      const response = await fetch('/api/match-mode/save-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: state.match.id,
          ...fieldsToUpdate,
        }),
      });

      const newState = (await response.json()) as { match?: Match };

      if (newState.match) {
        setMatchData(newState.match);

        if (newState.match.matchStatus === 'ended') {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
          localStorage.removeItem(newState.match.uid!);
        }
      }

      dispatch({ type: 'MATCH_SAVE_ENTRY', data: newState });
    },
    [dispatch],
  );

  const refreshMatch = useCallback(
    async (state: { match: Match }) => {
      const response = await fetch('/api/match-mode/get-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: state.match.id,
        }),
      });
      const newState = (await response.json()) as { entry?: Match };
      if (newState.entry) {
        /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
        const substitutes =
          filterByReference(
            newState.entry.initialLineUp,
            newState.entry.currentLineUp,
          ) || [];
        const absents =
          filterByReference(
            newState.entry.team![0].players,
            newState.entry.initialLineUp,
          ) || [];
        /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
        setMatchData({ ...newState.entry, substitutes, absents });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger; adding the flagged deps would shift this shared provider's effect/identity timing across every match consumer
    [dispatch],
  );

  const matchLogValue = useMemo(
    () => ({
      ...state,
      saveMatch,
      saveLocalMatchLogState,
      setMatchTime,
      refreshMatch,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger; adding the flagged deps would shift this shared provider's effect/identity timing across every match consumer
    [state],
  );

  // TODO maybe not pass any state values for non match mode users
  const matchContextStateValues: MatchContextValue = {
    ...matchLogValue,
    dispatch,
    enabled,
    team,
    match: matchData,
    setMatchData,
    timer: {
      time,
      start,
      pause,
      reset,
      status,
      advanceTime,
    },

    tabNav,
    setTabNav,
    showSidebar,
    setShowSidebar,
    sidebarTabNav,
    setSidebarTabNav,
    showEvaluation,
    setShowEvaluation,

    // TODO to discuss
    selectedPlayer,
    setSelectedPlayer,
    playerFormationRef,
    setPlayerFormationRef,
  };

  return (
    <MatchContext.Provider value={matchContextStateValues}>
      {children}
    </MatchContext.Provider>
  );
};

export default MatchProvider;

export const useMatchLog = () => {
  const context = useContext(MatchContext);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
  if (context === undefined) {
    throw new Error(`useMatch must be used within a MatchProvider`);
  }
  return context;
};
